import { Logger, SingleIndex } from '@deqo/foundation';
import { Component, ObjectManager } from '../../../concepts/objects';
import { Physical } from '../../canvas/entities/Physical';
import { ActiveObject, resolve } from '../../Container';
import { AutoLayout } from './AutoLayout';
import { AutoLayoutEntry } from './AutoLayoutAssocs';

const metrics = Logger.get('app.metrics');

export class AutoLayoutIndex extends ActiveObject {

    @resolve(ObjectManager) private objects: ObjectManager;

    private readonly forwardIndex = new SingleIndex<string, string>();
    private readonly reverseIndex = new Map<string, string>();
    private readonly invalidations = new Set<string>();

    public start(): void {
        this.rebuild();
        this.objects.on('updated', this.onUpdate);
    }

    public findElements(containerOrId: AutoLayout | string): ReadonlySet<Physical> {
        const containerId = typeof containerOrId === 'string' ? containerOrId : containerOrId.id;
        this.refresh();
        const elmts = new Set<Physical>();
        for (const id of this.forwardIndex.get(containerId)) {
            elmts.add(this.objects.get(id, Physical));
        }
        return elmts;
    }

    public findContainer(elementOrId: Physical | string) {
        const elementId = typeof elementOrId === 'string' ? elementOrId : elementOrId.id;
        this.refresh();
        const id = this.reverseIndex.get(elementId);
        if (id) {
            return this.objects.get(id, AutoLayout);
        }
        return null;
    }

    private rebuild() {
        const t = performance.now();

        const results = this.objects.findAll(x => x.is(Physical)) as Physical[];
        results.forEach(x => this.add(x));
        metrics.log('AutoLayoutIndex.rebuild', `${performance.now() - t}ms`);
    }

    private refresh() {
        for (const p of this.invalidations) {
            this.remove(p);
            this.add(p);
        }
        this.invalidations.clear();
    }

    private add(elementOrElementId: string | Physical) {
        const inst = typeof elementOrElementId === 'string' ? this.objects.getMaybe(elementOrElementId, Physical) : elementOrElementId;
        if (!inst?.alive) return null;
        const elementId = inst.id;
        const { ownerId } = AutoLayoutEntry.get(inst) ?? {};
        if (ownerId !== null && ownerId !== undefined) {
            this.forwardIndex.add(ownerId, elementId);
            this.reverseIndex.set(elementId, ownerId);
            return ownerId;
        }
        return null;
    }

    private remove(elementId: string) {
        const containerId = this.reverseIndex.get(elementId);
        if (containerId) {
            this.forwardIndex.remove(containerId, elementId);
            this.reverseIndex.delete(elementId);
            return containerId;
        }
        return null;
    }

    private onUpdate = (affected: Component[]) => {
        for (const c of affected) {
            if (c.is(Physical)) {
                this.invalidations.add(c.id);
            }
        }
        this.refresh();
    };
}
